import { Box, Card, NavList } from '@deckee/deck-hand';
import React, { FC, MouseEvent, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { captureEvent } from '../../../../services/event-capture';
import { hasFeatureEnabled } from '../../../../services/feature';
import { openWithInAppBrowser } from '../../../../services/in-app-browser';
import { AppState, useAppDispatch } from '../../../../services/store';
import { fetchPartners } from '../../../../services/store/modules/partners';
import { Partner } from '../../../../services/store/modules/partners/partners.models';
import { getAllPartners } from '../../../../services/store/modules/partners/partners.selectors';
import useIsInBounds from '../../../hooks/use-is-in-bounds';
import BasicDrawerLayout from '../../../layouts/BasicDrawerLayout/BasicDrawerLayout';
import { LayoutBody } from '../../../layouts/components/LayoutBody/LayoutBody';
import UserHeaderBar from '../../../shared/UserHeaderBar/UserHeaderBar';
import {
  identityCard,
  messages,
  shieldTick,
  shop,
  spanner,
} from '../../../shared/vector-images';
import reminderImage from '../assets/reminder_button_image.jpg';
import EmptyInfoPanel from '../../../shared/EmptyInfoPanel/EmptyInfoPanel';
import SafetyGearContainer from '../components/SafetyGearContainer/SafetyGearContainer';
import SafetyGearListItem from '../components/SafetyGearListItem/SafetyGearListItem';

const StyledPartnerIcon = styled.img`
  max-height: 50px;
  max-width: 40px;
  margin-right: 6px;
`;

const StyledReminderButtonImage = styled.img`
  max-width: 80px;

  @media screen and (min-width: 350px) {
    max-width: 120px;
  }
  @media screen and (min-width: 650px) {
    max-width: 150px;
  }
  @media screen and (min-width: 800px) {
    max-width: 120px;
  }
`;

const PrepareHomePage: FC = () => {
  const history = useHistory();
  const canPrepareAus = useIsInBounds('AUSTRALIA');
  const canPrepareUSA = useIsInBounds('UNITEDSTATES');
  const canPrepareHawaii = useIsInBounds('HAWAII');
  const canPrepareAlaska = useIsInBounds('ALASKA');

  const dispatch = useAppDispatch();
  const bounds = useSelector(
    (state: AppState) =>
      state.appControls.currentMapParams.viewPortDetails.bounds,
  );
  const partners = useSelector(getAllPartners);
  useEffect(() => {
    const promise = dispatch(fetchPartners());

    return () => {
      promise.abort();
    };
  }, [dispatch, bounds]);

  const safetyRecommendationsEnabled = hasFeatureEnabled(
    'safety-recommendations',
  );

  const handleSelectPartner = (partner: Partner) => {
    captureEvent({
      type: 'prepare: click partner',
      payload: {
        partnerId: partner.id,
        partnerName: partner.name,
      },
    });

    history.push(`/maps/partner/${partner.slug}`);
  };

  const canPrepare = safetyRecommendationsEnabled
    ? canPrepareAus || canPrepareUSA || canPrepareHawaii || canPrepareAlaska
    : false;

  const handleClickMenuItem = async (
    name: string,
    e?: MouseEvent<HTMLElement>,
    url?: string,
  ) => {
    captureEvent({
      type: `prepare: click menu item (${name})`,
      payload: {},
      sendToAmplitude: true,
    });

    // Trying to detect if it's an internal URL. if it is, just return true so the default navigation can happen
    if (url?.charAt(0) === '/' && url?.charAt(1) !== '/') {
      return true;
    }

    if (url) {
      try {
        e?.preventDefault();
        e?.stopPropagation();
        await openWithInAppBrowser({ url });
      } catch (err) {
        // Something has gone wrong, just navigate as usual
        return true;
      }
    }

    // Prevent the default navigation
    return false;
  };
  return (
    <BasicDrawerLayout renderHeaderBar={<UserHeaderBar heading="Prepare" />}>
      <LayoutBody>
        {canPrepare && <SafetyGearContainer />}
        {partners && partners.length > 0 && (
          <Card>
            <Card.Heading>Partners and resources</Card.Heading>

            <Card.Body>
              <NavList edge grouped>
                {partners.map((partner) => (
                  <NavList.Item
                    chevron
                    key={partner.id}
                    onClick={() => handleSelectPartner(partner)}
                  >
                    {partner.icon && (
                      <StyledPartnerIcon alt="" src={partner.icon} />
                    )}
                    <Box>{partner.name}</Box>
                  </NavList.Item>
                ))}
              </NavList>
            </Card.Body>
          </Card>
        )}
        <Card>
          <Card.Heading>Be prepared</Card.Heading>
          <Card.Body>
            <NavList edge>
              {hasFeatureEnabled('deckee-shop') && (
                <SafetyGearListItem
                  onClick={(e, url) =>
                    handleClickMenuItem('Shop deals', e, url)
                  }
                  href="//shop.deckee.com"
                  heading="Shop deals on gear"
                  description="Get safety equipment and more"
                  icon={shop()}
                />
              )}

              {hasFeatureEnabled('insurance') && (
                <SafetyGearListItem
                  onClick={(e, url) =>
                    handleClickMenuItem('Boat insurance', e, url)
                  }
                  href="/maps/insurance"
                  heading="Get a boat insurance quote"
                  description="Protect your asset"
                  icon={shieldTick()}
                />
              )}
              {hasFeatureEnabled('courses') && (
                <SafetyGearListItem
                  onClick={(e, url) =>
                    handleClickMenuItem('Book courses', e, url)
                  }
                  href="/maps/courses"
                  heading="Book a course"
                  description="Improve your confidence"
                  icon={identityCard()}
                />
              )}
              <SafetyGearListItem
                onClick={(e, url) =>
                  handleClickMenuItem('Find services', e, url)
                }
                href="/maps/marketplace/categories"
                heading="Discover local services"
                description="Find a trusted provider near you"
                icon={spanner()}
              />
              <SafetyGearListItem
                onClick={(e, url) =>
                  handleClickMenuItem('Community chat', e, url)
                }
                href="//community.deckee.com/"
                heading="Chat with the community"
                description="Learn from others on the water"
                icon={messages()}
              />
            </NavList>
          </Card.Body>
        </Card>
        <Box
          mb="2"
          bg="white"
          borderRadius="12px"
          display="flex"
          flex="1 1 auto"
          justifyContent="flex-start"
          alignItems="center"
          p={3}
          as="a"
          href="/maps/reminders"
          color="black"
          onClick={async (e: MouseEvent<HTMLElement>) => {
            e.preventDefault();
            e.stopPropagation();
            const shouldNavigate = await handleClickMenuItem(
              'Reminders',
              e,
              '/maps/reminders',
            );
            if (shouldNavigate) {
              history.push('/maps/reminders');
            }
          }}
        >
          <Box>
            <StyledReminderButtonImage
              src={reminderImage}
              alt="Reminders - Get notified when your equipment is expiring"
            />
          </Box>
          <Box pl={4} py={3} hyphens="manual">
            <Box mb={2} fontSize={2} fontWeight="bold">
              Add your reminders
            </Box>
            <Box color="darkergrey">
              Get notified when your equipment is expiring
            </Box>
          </Box>
        </Box>
        {!canPrepare && (
          <Card>
            <Card.Heading>Mandatory equipment</Card.Heading>
            <Card.Body>
              <EmptyInfoPanel>
                <strong>Coming soon:</strong> Check the mandatory equipment for
                your watercraft on any waterway.
              </EmptyInfoPanel>
            </Card.Body>
          </Card>
        )}
      </LayoutBody>
    </BasicDrawerLayout>
  );
};

export default PrepareHomePage;
